import { useState, useCallback, useRef, useEffect } from "react";
import { spliceAudio } from "../utils/spliceAudio";

/**
 * Loop-record hook — records within A-B loop markers and splices into the take.
 *
 * State machine:
 *   idle -> pre_roll -> recording -> splicing -> preview -> idle
 *
 * The caller provides loopRegion (in take-time) from the WaveSurfer region.
 * Pre-roll plays beat + take before the region start for context,
 * then auto-arms recording and auto-stops at region end.
 */
const useLoopRecord = ({
  takeWaveSurfer,
  beatWaveSurfer,
  recorder,
  getTakeAudioBuffer,
  loopRegion, // {start, end} in take-time seconds
  shiftOffsetSec = 0,
  preRollDuration = 3,
}) => {
  const [loopRecordState, setLoopRecordState] = useState("idle");
  const [splicedBlob, setSplicedBlob] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const preRollTimerRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const splicingStartedRef = useRef(false);
  const capturedBlobRef = useRef(null);

  // ---- Clear all timers ----

  const clearTimers = useCallback(() => {
    if (preRollTimerRef.current !== null) {
      clearTimeout(preRollTimerRef.current);
      preRollTimerRef.current = null;
    }
    if (autoStopTimerRef.current !== null) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  // ---- Public API ----

  const startLoopRecord = useCallback(() => {
    if (!loopRegion) {
      setError("No loop region set");
      return;
    }
    if (
      !Number.isFinite(loopRegion.start) ||
      !Number.isFinite(loopRegion.end) ||
      loopRegion.start >= loopRegion.end
    ) {
      setError("Invalid loop region");
      return;
    }
    if (!beatWaveSurfer) {
      setError("Beat player not ready");
      return;
    }
    if (!recorder) {
      setError("Recorder not available");
      return;
    }

    const beatDur = beatWaveSurfer.getDuration();
    if (!beatDur || beatDur <= 0) {
      setError("Beat audio not loaded");
      return;
    }

    setError(null);
    setSplicedBlob(null);
    setLoopRecordState("pre_roll");

    // Pre-warm mic during pre-roll so recording starts instantly
    if (recorder.requestMicPermission) {
      recorder.requestMicPermission().catch(() => {
        // Permission error will be caught when startRecording is called
      });
    }

    // loopRegion is in take-time — convert to beat-time for seeking
    const regionStartBeat = loopRegion.start + shiftOffsetSec;
    const preRollStartBeat = Math.max(0, regionStartBeat - preRollDuration);

    // Seek and play the beat
    beatWaveSurfer.seekTo(preRollStartBeat / beatDur);
    beatWaveSurfer.play();

    // Seek and play take if audio exists at the pre-roll position
    if (takeWaveSurfer) {
      const takeDur = takeWaveSurfer.getDuration();
      if (takeDur > 0) {
        const preRollTakeTime = preRollStartBeat - shiftOffsetSec;
        if (preRollTakeTime >= 0 && preRollTakeTime < takeDur) {
          takeWaveSurfer.seekTo(preRollTakeTime / takeDur);
          takeWaveSurfer.play();
        }
      }
    }

    const preRollMs = (regionStartBeat - preRollStartBeat) * 1000;
    const regionDurationMs = (loopRegion.end - loopRegion.start) * 1000;

    // After pre-roll completes, auto-arm recording
    preRollTimerRef.current = setTimeout(async () => {
      preRollTimerRef.current = null;
      setLoopRecordState("recording");

      try {
        await recorder.startRecording();
      } catch (err) {
        setError(
          "Failed to start recording: " + (err.message || "Unknown error"),
        );
        if (takeWaveSurfer) takeWaveSurfer.pause();
        beatWaveSurfer.pause();
        setLoopRecordState("idle");
        return;
      }

      // Auto-stop after region duration
      autoStopTimerRef.current = setTimeout(() => {
        autoStopTimerRef.current = null;
        recorder.stopRecording();
        if (takeWaveSurfer) takeWaveSurfer.pause();
        beatWaveSurfer.pause();
        // Splice effect will fire when recorder.audioBlob becomes available
        setLoopRecordState("splicing");
      }, regionDurationMs);
    }, preRollMs);
  }, [
    loopRegion,
    takeWaveSurfer,
    beatWaveSurfer,
    recorder,
    preRollDuration,
    shiftOffsetSec,
  ]);

  const stopLoopRecord = useCallback(() => {
    // Clear auto-stop timer (user is manually stopping early)
    if (autoStopTimerRef.current !== null) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }

    if (recorder) {
      try {
        recorder.stopRecording();
      } catch {
        // Ignore
      }
    }

    if (takeWaveSurfer) {
      try {
        takeWaveSurfer.pause();
      } catch {
        // Ignore
      }
    }
    if (beatWaveSurfer) {
      try {
        beatWaveSurfer.pause();
      } catch {
        // Ignore
      }
    }

    setLoopRecordState("splicing");
  }, [recorder, takeWaveSurfer, beatWaveSurfer]);

  const cancelLoopRecord = useCallback(() => {
    clearTimers();

    if (
      recorder &&
      (loopRecordState === "recording" || loopRecordState === "pre_roll")
    ) {
      try {
        recorder.cancelRecording();
      } catch {
        // Ignore errors from cancelling an inactive recorder
      }
    }

    if (takeWaveSurfer) {
      try {
        takeWaveSurfer.pause();
      } catch {
        // Ignore
      }
    }
    if (beatWaveSurfer) {
      try {
        beatWaveSurfer.pause();
      } catch {
        // Ignore
      }
    }

    setLoopRecordState("idle");
    setSplicedBlob(null);
    setIsProcessing(false);
    setError(null);
  }, [clearTimers, recorder, loopRecordState, takeWaveSurfer, beatWaveSurfer]);

  const acceptSplice = useCallback(() => {
    setLoopRecordState("idle");
    setIsProcessing(false);
    setError(null);
  }, []);

  const retrySplice = useCallback(() => {
    setSplicedBlob(null);
    setIsProcessing(false);
    setError(null);
    setLoopRecordState("idle");
  }, []);

  // ---- Watch for recorder's audioBlob when in splicing state ----

  useEffect(() => {
    if (loopRecordState !== "splicing") {
      splicingStartedRef.current = false;
      return;
    }
    if (!recorder?.audioBlob) return;
    if (!loopRegion) return;
    if (splicingStartedRef.current) return; // Already processing

    // Capture the blob and immediately clear the recorder so no "Save Take"
    // popup can appear from RecordingControls seeing a stale audioBlob.
    splicingStartedRef.current = true;
    capturedBlobRef.current = recorder.audioBlob;
    if (recorder.resetRecording) recorder.resetRecording();

    const processSplice = async () => {
      setIsProcessing(true);
      setError(null);

      try {
        const originalBuffer = await getTakeAudioBuffer();
        const result = await spliceAudio(
          originalBuffer,
          capturedBlobRef.current,
          loopRegion.start,
          loopRegion.end,
        );
        setSplicedBlob(result);
        setLoopRecordState("preview");
      } catch (err) {
        setError(err.message || "Splice failed");
        setLoopRecordState("idle");
      } finally {
        setIsProcessing(false);
        capturedBlobRef.current = null;
      }
    };

    processSplice();
  }, [
    loopRecordState,
    recorder?.audioBlob,
    loopRegion,
    getTakeAudioBuffer,
    recorder,
  ]);

  // ---- Cleanup timers on unmount ----

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    loopRecordState,
    splicedBlob,
    isProcessing,
    error,

    startLoopRecord,
    stopLoopRecord,
    cancelLoopRecord,
    acceptSplice,
    retrySplice,
  };
};

export default useLoopRecord;
